import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export const useDeepLinking = () => {
  const navigate = useNavigate();
  const { checkSubscription } = useAuth();
  const { toast } = useToast();

  // Keep latest references without re-registering native listeners
  const navigateRef = useRef(navigate);
  const checkSubscriptionRef = useRef(checkSubscription);
  const toastRef = useRef(toast);

  useEffect(() => {
    navigateRef.current = navigate;
    checkSubscriptionRef.current = checkSubscription;
    toastRef.current = toast;
  }, [navigate, checkSubscription, toast]);

  useEffect(() => {
    const handleAppUrlOpen = async (event: URLOpenListenerEvent) => {
      const url = event.url;
      console.log('[DeepLink] Received URL:', url);

      // Handle OAuth callback deep link: translatify://oauth#access_token=...
      if (url.includes('oauth')) {
        console.log('[DeepLink] Handling OAuth callback');

        try {
          // Close the external browser
          await Browser.close().catch(() => {});

          // Extract tokens from the URL hash fragment
          const hashPart = url.split('#')[1];
          if (hashPart) {
            const params = new URLSearchParams(hashPart);
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');

            if (accessToken && refreshToken) {
              console.log('[DeepLink] Setting session from OAuth tokens');
              const { error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (error) {
                console.error('[DeepLink] Error setting session:', error);
                toastRef.current({
                  title: 'Sign in failed',
                  description: 'Could not complete sign in. Please try again.',
                  variant: 'destructive',
                });
              } else {
                console.log('[DeepLink] Session set successfully');
                toastRef.current({
                  title: 'Welcome!',
                  description: 'You have been signed in successfully.',
                });
                
                // Small delay to let auth state propagate
                setTimeout(() => {
                  navigateRef.current('/chats');
                }, 500);
              }
              return;
            }
          }

          // If we couldn't extract tokens, try refreshing the session
          const { data: { session }, error } = await supabase.auth.refreshSession();
          if (session) {
            console.log('[DeepLink] Session refreshed after OAuth');
            navigateRef.current('/chats');
          } else {
            console.error('[DeepLink] No session after OAuth:', error);
            toastRef.current({
              title: 'Sign in failed',
              description: 'Could not complete sign in. Please try again.',
              variant: 'destructive',
            });
          }
        } catch (err) {
          console.error('[DeepLink] OAuth callback error:', err);
        }
        return;
      }

      // Handle checkout success deep link
      if (url.includes('checkout/success')) {
        console.log('[DeepLink] Handling checkout success');

        const {
          data: { session },
          error,
        } = await supabase.auth.refreshSession();
        if (error) {
          console.error('[DeepLink] Session refresh error:', error);
        } else {
          console.log('[DeepLink] Session refreshed successfully', {
            hasSession: Boolean(session),
          });
        }

        toastRef.current({
          title: 'Payment successful!',
          description: 'Your subscription is now active.',
        });

        setTimeout(() => {
          void checkSubscriptionRef.current();
          navigateRef.current('/chats');
        }, 1000);
      }

      // Handle checkout cancel deep link
      if (url.includes('checkout/cancel')) {
        toastRef.current({
          title: 'Payment cancelled',
          description: 'You can try again anytime.',
          variant: 'destructive',
        });

        navigateRef.current('/chats');
      }
    };

    // Register the deep link listener ONCE
    const sub = App.addListener('appUrlOpen', handleAppUrlOpen);

    return () => {
      sub.then((s) => s.remove());
    };
  }, []);
};
